import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  ErrorLabel,
  Password,
} from '@ibsys/kit';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/entities/auth';
import {
  PasswordRulesHint,
  useEnglishLayoutInput,
  usePasswordRulesField,
  usePasswordStrengthValidator,
  useSetOwnPassword,
} from '@/entities/users';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api';
import { useAuth } from '@/shared/hooks';
import { TemporaryPasswordExpired } from './TemporaryPasswordExpired';

type FormValues = {
  newPassword: string;
  confirmPassword: string;
};

/**
 * Модальное окно установки постоянного пароля. Показывается вместо приложения, пока профиль
 * текущего пользователя требует заменить временный пароль, — в том числе после перезагрузки страницы.
 * Закрытие по клику вне окна и по Esc заблокировано — обязательное действие.
 */
export const TemporaryPasswordModal: FC = () => {
  const { t } = useTranslation('auth');
  const user = useAuthStore((s) => s.user);
  const setIsTempPassword = useAuthStore((s) => s.setIsTempPassword);
  const { logout } = useAuth();
  const { mutateAsync: setOwnPassword, isPending } = useSetOwnPassword();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  const { control, handleSubmit, setError, clearErrors } = useForm<FormValues>({
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const checkPasswordStrength = usePasswordStrengthValidator();
  const { applyServerRefusal } = usePasswordRulesField({ control, setError, clearErrors, name: 'newPassword' });
  const englishLayoutInput = useEnglishLayoutInput();

  const submit = handleSubmit(async ({ newPassword }) => {
    setSubmitError(null);
    try {
      await setOwnPassword({ password: newPassword });
      // Окно уступает место приложению
      setIsTempPassword(false);
    } catch (error) {
      if (applyServerRefusal(error)) {
        return;
      }
      // Замену отклоняет с этим кодом только истёкший срок пароля: от чужого имени окно не показывается
      if (getApiErrorStatus(error) === 403) {
        setIsExpired(true);
        return;
      }
      setSubmitError(getApiErrorMessage(error));
    }
  });

  return (
    <DialogRoot open modal>
      <DialogContent
        showCloseButton={false}
        className="max-w-96"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('tempPasswordModal.title')}</DialogTitle>
        </DialogHeader>
        {isExpired ? (
          <TemporaryPasswordExpired onLogout={logout} />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <p className="text-sm text-foreground">{t('tempPasswordModal.description')}</p>
            <Password
              {...englishLayoutInput}
              control={control}
              name="newPassword"
              label={
                <>
                  {t('tempPasswordModal.newPassword')}
                  <PasswordRulesHint />
                </>
              }
              autoComplete="new-password"
              rules={{
                required: { value: true, message: t('tempPasswordModal.errorNewPasswordEmpty') },
                // Владелец пароля — вошедший пользователь. Текущий, временный, пароль форме не известен —
                // его сверяет сервер
                validate: (password) =>
                  checkPasswordStrength(password, {
                    email: user?.email,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                  }),
              }}
            />
            <Password
              {...englishLayoutInput}
              control={control}
              name="confirmPassword"
              label={t('tempPasswordModal.confirmPassword')}
              autoComplete="new-password"
              rules={{
                required: { value: true, message: t('tempPasswordModal.errorConfirmPasswordEmpty') },
                validate: (value, formValues) =>
                  value === formValues.newPassword || t('tempPasswordModal.errorPasswordsDoNotMatch'),
              }}
            />
            {submitError && <ErrorLabel className="text-center">{submitError}</ErrorLabel>}
            <DialogFooter>
              <Button type="submit" className="w-full" isLoading={isPending}>
                {t('tempPasswordModal.save')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </DialogRoot>
  );
};
